/**
 * <s-gallery-thumbs> — thumbnail strip under the product gallery.
 *
 * Thumbnail buttons target this element with `commandfor` + `command="--show-media"`
 * and carry the slide index in `value`. The element scrolls the sibling
 * `[data-product-gallery]` scroller (inside the same <s-product-image-zoom>) to that
 * slide, and keeps `aria-current` on the thumbnail of the slide in view as the buyer
 * swipes.
 */
class SGalleryThumbs extends HTMLElement {
  #ac;
  #gallery;
  #frame = 0;

  connectedCallback() {
    this.#gallery = this.closest('s-product-image-zoom')?.querySelector('[data-product-gallery]');
    if (!this.#gallery) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-media') return;
      const index = Number(event.source?.value ?? 0);
      this.#showSlide(index);
    }, opts);

    this.#gallery.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncFromScroll());
    }, { ...opts, passive: true });
    this.#watchVariant(opts);
  }

  /** Jump to the selected variant's photo (e.g. picking a color) using the variant → slide map. */
  #watchVariant(opts) {
    let map = {};
    try {
      map = JSON.parse(this.dataset.variantMedia || '{}');
    } catch {
      return;
    }
    const productId = this.id.replace('gallery-thumbs-', '');
    const form = document.querySelector(`[data-product-form][data-product-id="${productId}"]`);
    if (!form) return;

    let lastVariant = form.querySelector('input[name="id"]')?.value;
    const sync = () => {
      const variantId = form.querySelector('input[name="id"]')?.value;
      if (!variantId || variantId === lastVariant) return;
      lastVariant = variantId;
      const index = map[variantId];
      if (typeof index === 'number') this.#showSlide(index);
    };

    form.addEventListener('change', () => setTimeout(sync, 0), opts);
    const observer = new MutationObserver(sync);
    observer.observe(form, { childList: true, subtree: true });
    opts.signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #showSlide(index) {
    const slides = this.#gallery.children;
    const slide = slides[index];
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#gallery.scrollTo({
      left: slide.offsetLeft - slides[0].offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#setActive(index);
  }

  #syncFromScroll() {
    const width = this.#gallery.clientWidth;
    if (!width) return;
    this.#setActive(Math.round(this.#gallery.scrollLeft / width));
  }

  #setActive(index) {
    this.querySelectorAll('button[command="--show-media"]').forEach((button, buttonIndex) => {
      button.setAttribute('aria-current', String(buttonIndex === index));
    });
  }
}

if (!customElements.get('s-gallery-thumbs')) {
  customElements.define('s-gallery-thumbs', SGalleryThumbs);
}
